import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, Typography } from '@mui/material';
import React from 'react';

import Loader from 'components/Loader';
import { Job, JobStatus, JobTimelineLogEntry, JobTimelineProgress, JobType } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';

const getStepHistory = (
  timeline: Job['timeline']
): { log: JobTimelineLogEntry[]; progress: JobTimelineProgress | null } => {
  if (!timeline || Array.isArray(timeline)) {
    return { log: [], progress: null };
  }
  return {
    log: Array.isArray(timeline.log) ? timeline.log : [],
    progress: timeline.progress ?? null,
  };
};

const StepEventIcon: React.FC<{ type?: string }> = ({ type }) => {
  if (type === 'error') {
    return <ErrorOutlineIcon color="error" fontSize="small" titleAccess="Error" />;
  }
  if (type === 'warning') {
    return <WarningAmberOutlinedIcon color="warning" fontSize="small" titleAccess="Warning" />;
  }
  return null;
};

/** Lists every step the ingest or tile generation worker recorded, oldest first, plus live progress while running. */
export const JobStepHistorySection: React.FC<{ job: Partial<Job> }> = ({ job }) => {
  const { log, progress } = getStepHistory(job.timeline);
  const isActive = job.status === JobStatus.Pending || job.status === JobStatus.Running;
  const showProgress = isActive && progress !== null;
  const supportsStepHistory = job.type === JobType.Ingest || job.type === JobType.TileGeneration;

  if (log.length === 0 && !showProgress && !supportsStepHistory) {
    return null;
  }

  return (
    <Grid item>
      <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="step-history-content" id="step-history-header">
          <Typography variant="h4">Steps</Typography>
        </AccordionSummary>
        <AccordionDetails id="step-history-content">
          <Stack spacing={1.5}>
            {showProgress && (
              <Loader
                inline
                message={`${progress.message} (${progress.percent}% overall)`}
                value={progress.percent}
                testId="job-step-progress"
              />
            )}
            {log.length > 0 ? (
              <Stack component="ol" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }} aria-label="Step history">
                {log.map((entry, index) => (
                  <Stack
                    component="li"
                    // Entries are append-only, so the index is stable.
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, minWidth: 150 }}>
                      {formatDateTimeForUser(entry.timestamp, { dateStyle: 'medium', timeStyle: 'medium' })}
                    </Typography>
                    <StepEventIcon type={entry.type} />
                    <Typography
                      variant="body2"
                      color={entry.type === 'error' ? 'error' : 'text.primary'}
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {entry.event}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Step history is unavailable for this job.
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
